import React, { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { PageProps, User } from "../types/auth";

// Import SVG icons
import TrashIcon from "../assets/icons/trash.svg";

import styles from './styles/MemberAttendancePage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Member extends User {
  role: string;
}

interface Attendance {
  attendance_id: number;
  attendance_date: string;
}

interface RouteParams {
  [key: string]: string | undefined;
  member_id?: string;
}

interface LocationState {
  selectedCurriculum?: number;
}

const MemberAttendancePage: React.FC<PageProps> = ({ token, selectedOrgID }) => {
  const { member_id } = useParams<RouteParams>();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState;

  const [member, setMember] = useState<Member | null>(null);
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [error, setError] = useState<string>("");
  const [attendanceToDelete, setAttendanceToDelete] = useState<Attendance | null>(null);
  const selectedCurriculum = locationState?.selectedCurriculum;

  useEffect(() => {
    const fetchMemberDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${member_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch member details.");
          return;
        }

        const data: Member = await response.json();
        setMember(data);
      } catch (err) {
        setError("An unexpected error occurred while fetching member details.");
      }
    };

    fetchMemberDetails();
    if (selectedCurriculum) fetchAttendances();
  }, [member_id, selectedCurriculum, token, selectedOrgID]);

  const fetchAttendances = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/attendances?user_id=${member_id}&curriculum_id=${selectedCurriculum}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to fetch attendances.");
        return;
      }

      const data: Attendance[] = await response.json();
      setAttendances(data || []);
    } catch (err) {
      setError("An unexpected error occurred while fetching attendances.");
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = e.target.value; // e.g., "2024-12-23"
    
    // Parse it manually to ensure it's treated as local time
    const [year, month, day] = selectedValue.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
  
    setSelectedDate(selectedValue);
  };

  const markAttendance = async () => {
    // Get timezone offset in hours:minutes format
    const date = new Date()
    const offsetMinutes = date.getTimezoneOffset()
    const offsetHours = Math.abs(Math.floor(offsetMinutes / 60)).toString().padStart(2, "0")
    const offsetMins = Math.abs(offsetMinutes % 60).toString().padStart(2, "0")
    const offsetSign = offsetMinutes <= 0 ? "+" : "-"

    
    // Combine selected date with midnight time and timezone
    const localISOString = `${selectedDate}T00:00:00${offsetSign}${offsetHours}:${offsetMins}`
    
    console.log("selectedDate: " + selectedDate);
    console.log("localISOString: " + localISOString);

    try {
      const response = await fetch(`${API_BASE_URL}/api/attendances`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: parseInt(member_id || "0", 10),
          curriculum_id: selectedCurriculum,
          attendance_date: localISOString,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to mark attendance.");
        return;
      }

      fetchAttendances(); // Refresh attendance list after marking attendance
    } catch (err) {
      setError("An unexpected error occurred while marking attendance.");
    }
  };

  const removeAttendance = async (attendanceId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/attendances/${attendanceId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to remove attendance.");
        return;
      }

      setAttendances((prev) => prev.filter((att) => att.attendance_id !== attendanceId));
      setAttendanceToDelete(null);
    } catch (err) {
      setError("An unexpected error occurred while removing attendance.");
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Member Attendance</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}

      <div className={styles.memberDetails}>
        <strong>Member:</strong>{" "}
        <a href={`/members/${member_id}`} className={styles.memberLink}>
          {member?.display_name ? `${member.display_name} (${member.username})` : member?.username}
        </a>
      </div>
      <div className={styles.datePicker}>
        <strong>Date:</strong>
        <input
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          className={styles.dateInput}
        />
      </div>
      <button className={styles.markAttendanceButton} onClick={markAttendance}>
        Mark Attendance
      </button>

      <h3 className={styles.sectionTitle}>Attendance Records</h3>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Date</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {attendances.length > 0 ? (
            attendances.map((attendance) => (
              <tr key={attendance.attendance_id} className={styles.tableRow}>
                <td className={styles.tableCell}>{new Date(attendance.attendance_date).toLocaleDateString()}</td>
                <td className={styles.tableCell}>
                  <button
                    className={styles.iconButton}
                    onClick={() => setAttendanceToDelete(attendance)}
                    title="Remove"
                  >
                    <img src={TrashIcon} alt="Remove" className={styles.actionIcon} />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className={styles.tableCell}>No attendance records found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {attendanceToDelete && (
        <>
          <div className={styles.dialogOverlay} onClick={() => setAttendanceToDelete(null)} />
          <div className={styles.dialog}>
            <p>
              Are you sure you want to remove this attendance record?<br /><br />
              This action CANNOT BE UNDONE.
            </p>
            <div className={styles.dialogButtons}>
              <button 
                onClick={() => removeAttendance(attendanceToDelete.attendance_id)} 
                className={styles.dialogButton}
              >
                Yes, Remove
              </button>
              <button 
                onClick={() => setAttendanceToDelete(null)} 
                className={styles.dialogCancelButton}
              >
                Cancel
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MemberAttendancePage;
